import * as bulmaToast from 'bulma-toast';
import { Options } from 'bulma-toast';
import { GenericObject } from '../rbz/types';
import _ from 'lodash';
import close from '../assets/icons/svgs/close.svg';
import isError from '../assets/icons/svgs/is-error.svg';
import isInfo from '../assets/icons/svgs/is-info.svg';
import isSuccess from '../assets/icons/svgs/is-success.svg';
import isWarning from '../assets/icons/svgs/is-warning.svg';
import { Composer } from 'vue-i18n';

const invalidityClasses = ` has-text-danger has-background-danger-light`;

// Validates an input based on given validator (Function / Boolean) and adds necessary classes if input is invalid
const validateInput = (event: Event, validator: ((event: Event) => boolean) | boolean) => {
  if (!(event instanceof Event)) {
    return false;
  }
  let className = (event.target as HTMLElement)?.className;
  let isValid;
  className = className.replace(`${invalidityClasses}`, '');
  if (typeof validator === 'function') {
    isValid = validator(event);
  } else {
    isValid = validator;
  }
  if (!isValid) {
    className += `${invalidityClasses}`;
  }
  (event.target as HTMLElement).className = className;
  return isValid;
};

// Clear invalidity classes from an input
const clearInputValidationClasses = (element: HTMLElement) => {
  if (!element) {
    return;
  }
  let className = element.className;
  className = className.replace(`${invalidityClasses}`, '');
  element.className = className;
};

// Generates a unique name in a given entities list
const generateUniqueEntityName = (
  originalName: string,
  entitiesList: string[],
  isCopy?: boolean,
  divider = ' '
) => {
  if (!originalName) {
    originalName = `new${divider}entity`;
  }
  let namePrefix = '';
  if (isCopy) {
    namePrefix = `copy${divider}of${divider}`;
  }
  let newName = `${namePrefix}${originalName}`;
  let counter = 1;
  while (
    _.some(entitiesList, (item) => {
      return item.toLowerCase() === newName.toLowerCase();
    })
  ) {
    counter++;
    newName = `${namePrefix}${originalName}(${counter})`;
  }
  return newName;
};

// Download data as file
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const downloadFile = (fileName: string, fileType: string, data: any) => {
  // Check if file type can be downloaded
  const recognizedDownloadFileTypes = ['json', 'pfx', 'pem'];
  if (!recognizedDownloadFileTypes.includes(fileType)) {
    console.log('Unable to download file, unknown file type');
    return;
  }
  let blob;
  if (fileType === 'json') {
    const content: BlobPart = JSON.stringify(data);
    blob = new Blob([content], {
      type: `application/${fileType}`,
    });
  }
  if (fileType === 'pfx' || fileType === 'pem') {
    blob = data;
  }
  // Create anchor element with download data
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = `${fileName}.${fileType}`;
  // Click initiates the download
  link.click();
};

// Default values for toast messages
bulmaToast.setDefaults({
  message: '',
  position: 'bottom-right',
  closeOnClick: false,
  pauseOnHover: true,
  duration: 3 * 1000,
  opacity: 1,
});

const toastIconsSrcMap = {
  close: close,
  'is-danger': isError,
  'is-info': isInfo,
  'is-success': isSuccess,
  'is-warning': isWarning,
} as GenericObject;

// Displays a toast message
const toast = (config: Options) => {
  if (typeof config.message === 'string') {
    const toastMessageWrapper = document.createElement('div');
    toastMessageWrapper.classList.add('rbz-notification');
    config.type = config.type || 'is-info';
    const iconSrc = toastIconsSrcMap[config.type];
    const iconElement = document.createElement('img');
    iconElement.classList.add('rbz-notification-icon');
    iconElement.src = iconSrc;
    toastMessageWrapper.appendChild(iconElement);
    const textElement = document.createElement('span');
    textElement.classList.add('rbz-notification-text');
    textElement.innerHTML = `${_.escape(config.message)}`;
    toastMessageWrapper.appendChild(textElement);
    const closeIconElement = document.createElement('img');
    closeIconElement.classList.add('rbz-notification-close-icon');
    closeIconElement.src = toastIconsSrcMap['close'];
    const destroyMessage = () => {
      closeIconElement.removeEventListener('click', destroyMessage);
      toastMessageWrapper.parentElement?.remove();
    };
    closeIconElement.addEventListener('click', destroyMessage);
    toastMessageWrapper.appendChild(closeIconElement);
    config.message = toastMessageWrapper;
  }
  bulmaToast.toast(config);
};

const removeExtraWhitespaces = (value: string) => {
  return value?.replace(/\s\s+/g, ' ') || '';
};

const amountSuffixFormatter = (value: number, translator: Composer['t']) => {
  const lookup = [
    { value: 1, translationKey: 'size.general.1e0' },
    { value: 1e3, translationKey: 'size.general.1e3' },
    { value: 1e6, translationKey: 'size.general.1e6' },
    { value: 1e9, translationKey: 'size.general.1e9' },
    { value: 1e12, translationKey: 'size.general.1e12' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = lookup
    .slice()
    .reverse()
    .find((item) => {
      return value >= item.value;
    });
  if (!item) {
    return '0';
  }
  const fixedPoint = item && value / item.value < 100 ? 1 : 0;
  const count = (value / item.value).toFixed(fixedPoint).toString().replace(rx, '$1');
  return translator(item.translationKey, { count: Number(count) });
};

const amountSuffixFormatterBytes = (value: number, translator: Composer['t']) => {
  if (!value || value <= 0) {
    return translator('size.bytes.1024pow0', { count: 0 });
  }
  const lookup = [
    { value: 1, translationKey: 'size.bytes.1024pow0' },
    { value: 1024, translationKey: 'size.bytes.1024pow1' },
    { value: Math.pow(1024, 2), translationKey: 'size.bytes.1024pow2' },
    { value: Math.pow(1024, 3), translationKey: 'size.bytes.1024pow3' },
    { value: Math.pow(1024, 4), translationKey: 'size.bytes.1024pow4' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = lookup
    .slice()
    .reverse()
    .find((item) => {
      return value >= item.value;
    });
  const count = (value / item.value).toFixed(2).toString().replace(rx, '$1');
  return translator(item.translationKey, { count: Number(count) });
};

const hexToRgbArray = (hex: string) => {
  let colors: string[];
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    colors = hex.substring(1).split('');
    if (colors.length === 3) {
      colors = [colors[0], colors[0], colors[1], colors[1], colors[2], colors[2]];
    }
    const color = '0x' + colors.join('');
    const colorNumber = Number(color);
    return [(colorNumber >> 16) & 255, (colorNumber >> 8) & 255, colorNumber & 255];
  }
};

const sortArrayByName = (docArray: GenericObject[]) => {
  if (!docArray) {
    return [];
  }
  return docArray.sort((a: GenericObject, b: GenericObject) => {
    let sortValueA: string = a.name || '';
    let sortValueB: string = b.name || '';
    const sortValueALowerCase: string = sortValueA.toString().toLowerCase();
    const sortValueBLowerCase: string = sortValueB.toString().toLowerCase();
    // only ignore case if the values are different from one another
    if (!_.isEqual(sortValueALowerCase, sortValueBLowerCase)) {
      sortValueA = sortValueALowerCase;
      sortValueB = sortValueBLowerCase;
    }
    if (sortValueA < sortValueB) {
      return -1;
    }
    if (sortValueA > sortValueB) {
      return 1;
    }
    return 0;
  });
};

const parseJwt = (token: string) => {
  const base64Url = token?.split('.')?.[1] || '';
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
  let parsedValue;
  try {
    parsedValue = JSON.parse(jsonPayload);
  } catch (_err) {
    parsedValue = {};
  }
  return parsedValue;
};

const isIP = (input: string) => {
  if (isIPv4(input)) {
    return 4;
  } else if (isIPv6(input)) {
    return 6;
  } else {
    return 0;
  }
};
const isIPv4 = (input: string) => {
  return /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))$/.test(
    input
  );
};
const isIPv6 = (input: string) => {
  return /^((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))$/.test(
    input
  );
};

const snakeCaseToCamelCase = (input: string) => {
  return input.replace(/_./g, (x) => x[1].toUpperCase());
};

const elementTreeOverflow = (element: Partial<HTMLElement>): boolean => {
  if (!element || !element.clientHeight || !element.clientWidth) {
    return false;
  }
  return elementTreeOverflowX(element) || elementTreeOverflowY(element);
};

const elementTreeOverflowX = (element: Partial<HTMLElement>): boolean => {
  const elementOverflowX = element.clientWidth < element.scrollWidth;
  if (elementOverflowX) {
    return true;
  }
  return _.some(element.children, (childElement: Partial<HTMLElement>) => {
    return elementTreeOverflowX(childElement);
  });
};

const elementTreeOverflowY = (element: Partial<HTMLElement>): boolean => {
  const elementOverflowY = element.clientHeight < element.scrollHeight;
  if (elementOverflowY) {
    return true;
  }
  return _.some(element.children, (childElement: Partial<HTMLElement>) => {
    return elementTreeOverflowY(childElement);
  });
};

const saveSessionData = (formData: Document | GenericObject, entityNotSaved: boolean) => {
  if (entityNotSaved) {
    sessionStorage.setItem('formData', JSON.stringify(formData));
  } else {
    sessionStorage.removeItem('formData');
  }
};

const clearSessionData = () => {
  sessionStorage.removeItem('formData');
};

const scrollIntoViewIfNotVisible = (element: HTMLElement) => {
  if (!element) {
    return;
  }
  if (element.getBoundingClientRect().bottom > window.innerHeight) {
    element.scrollIntoView(false);
  }
  if (element.getBoundingClientRect().top < 0) {
    element.scrollIntoView();
  }
};

export interface ToastErrorWithDetailsOptions {
  duration?: number;
  extraClasses?: string;
  /** Label for the expand button when details are hidden. Default: "Show details" */
  showDetailsLabel?: string;
  /** Label for the collapse button when details are visible. Default: "Hide details" */
  hideDetailsLabel?: string;
}

/**
 * Displays an error toast with optional expandable details.
 * @param failureMessage - Main title (always visible).
 * @param details - Optional text shown only when the user clicks "Show details".
 */
const toastErrorWithDetails = (
  failureMessage: string,
  details?: string,
  options: ToastErrorWithDetailsOptions = {}
) => {
  const {
    duration = 3 * 1000,
    extraClasses = '',
    showDetailsLabel = 'Show details',
    hideDetailsLabel = 'Hide details',
  } = options;

  const detailsTrimmed = details?.trim() ?? '';
  const hasExpandableDetails = detailsTrimmed.length > 0;

  const toastMessageWrapper = document.createElement('div');
  toastMessageWrapper.classList.add('rbz-notification', 'rbz-notification-error-details');

  const iconElement = document.createElement('img');
  iconElement.classList.add('rbz-notification-icon');
  iconElement.src = toastIconsSrcMap['is-danger'];
  toastMessageWrapper.appendChild(iconElement);

  const contentWrapper = document.createElement('div');
  contentWrapper.classList.add('rbz-notification-content');

  const summaryElement = document.createElement('div');
  summaryElement.classList.add(
    'rbz-notification-text',
    'rbz-notification-summary',
    'white-space-pre-line'
  );
  summaryElement.textContent = failureMessage.trim();
  contentWrapper.appendChild(summaryElement);

  if (hasExpandableDetails) {
    const toggleButton = document.createElement('button');
    toggleButton.type = 'button';
    toggleButton.classList.add('rbz-notification-toggle-details');
    toggleButton.textContent = showDetailsLabel;
    toggleButton.setAttribute('aria-expanded', 'false');

    const detailsContainer = document.createElement('div');
    detailsContainer.classList.add('rbz-notification-details');
    detailsContainer.setAttribute('hidden', '');
    const detailsPre = document.createElement('pre');
    detailsPre.classList.add('rbz-notification-text', 'rbz-notification-text-scrollable');
    detailsPre.textContent = detailsTrimmed;
    detailsContainer.appendChild(detailsPre);

    toggleButton.addEventListener('click', () => {
      const isExpanded = !detailsContainer.hasAttribute('hidden');
      if (isExpanded) {
        detailsContainer.setAttribute('hidden', '');
        toggleButton.textContent = showDetailsLabel;
        toggleButton.setAttribute('aria-expanded', 'false');
      } else {
        detailsContainer.removeAttribute('hidden');
        toggleButton.textContent = hideDetailsLabel;
        toggleButton.setAttribute('aria-expanded', 'true');
      }
    });

    contentWrapper.appendChild(toggleButton);
    contentWrapper.appendChild(detailsContainer);
  }

  toastMessageWrapper.appendChild(contentWrapper);

  const closeIconElement = document.createElement('img');
  closeIconElement.classList.add('rbz-notification-close-icon');
  closeIconElement.src = toastIconsSrcMap['close'];
  const destroyMessage = () => {
    closeIconElement.removeEventListener('click', destroyMessage);
    toastMessageWrapper.parentElement?.remove();
  };
  closeIconElement.addEventListener('click', destroyMessage);
  toastMessageWrapper.appendChild(closeIconElement);

  bulmaToast.toast({
    message: toastMessageWrapper,
    type: 'is-danger',
    duration,
    extraClasses: extraClasses.trim() || undefined,
  });
};

export default {
  name: 'Utils',
  validateInput,
  clearInputValidationClasses,
  generateUniqueEntityName,
  downloadFile,
  toast,
  removeExtraWhitespaces,
  amountSuffixFormatter,
  amountSuffixFormatterBytes,
  hexToRgbArray,
  sortArrayByName,
  parseJwt,
  isIP,
  isIPv4,
  isIPv6,
  snakeCaseToCamelCase,
  elementTreeOverflow,
  saveSessionData,
  clearSessionData,
  scrollIntoViewIfNotVisible,
  toastErrorWithDetails,
};
